import {
  Either,
  HomeFailure,
  HomeSectionType,
  left,
  right,
} from "../../domain";
import {
  HomeContent,
  HomeRepository,
} from "../../domain/repositories/home-repository";
import { HomeRemoteDataSourceError } from "../core";
import { HomeRemoteDataSource } from "../data-sources";

export class HomeRepositoryImpl implements HomeRepository {
  constructor(private readonly remoteDataSource: HomeRemoteDataSource) {}

  private handleError(error: HomeRemoteDataSourceError): HomeFailure {
    if (error === "NetworkError") return HomeFailure.networkFailure();
    return HomeFailure.serverFailure();
  }

  public async getHomeContent(): Promise<Either<HomeFailure, HomeContent>> {
    try {
      const res = await this.remoteDataSource.getHomeContent();
      return right(res);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async addSliderItem(
    link: string,
    image: File
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.addSliderItem(link, image);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async deleteSliderItem(
    id: string
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.deleteSliderItem(id);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editSliderItem(
    id: string,
    info: { link?: string | undefined; image?: File | undefined }
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.editSliderItem(id, info);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async addHomeSection(
    type: HomeSectionType,
    images: { link: string; image: File }[],
    products: number[],
    title?: string,
    description?: string
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.addHomeSection(
        type,
        images,
        products.map((num) => num.toString()),
        title,
        description
      );
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async deleteHomeSection(
    id: string
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.deleteHomeSection(id);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editHomeSectionInfo(
    id: string,
    info: {
      title?: string;
      description?: string;
      active?: boolean;
      products?: string[];
    }
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.editHomeSectionInfo(id, info);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editHomeSectionImage(
    id: string,
    info: { link?: string; image?: File }
  ): Promise<Either<HomeFailure, null>> {
    try {
      await this.remoteDataSource.editHomeSectionImage(id, info);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }
}
